import {
    forwardRef,
    useEffect,
    useId,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
    type FocusEvent,
    type MouseEvent as ReactMouseEvent,
} from 'react'

import { CKEditor as CKEditorBase } from '@ckeditor/ckeditor5-react'

import { KEY } from './key'

import { createEditorConfig, DecoupledEditor, pinBalloonToolbarAboveSelection } from './config'
import { BalloonToolbarTooltip } from './BalloonToolbarTooltip'
import { Footer } from './Footer'
import { ErrorIcon } from './icons'
import { cx, isEmptyHtml, stripHtml, toCssSize } from './utils'

import type { Editor, EventInfo } from 'ckeditor5'
import type { CKEditorHandle, CKEditorProps } from './types'

import 'ckeditor5/ckeditor5.css'
import './CKEditor.scss'

export const CKEditor = forwardRef<CKEditorHandle, CKEditorProps>(function CKEditor(
    {
        value,
        defaultValue = '',
        onChange,
        onFocus,
        onBlur,
        onReady,
        placeholder = 'Add a comment...',
        disabled = false,
        error,
        className,
        style,
        id,
        name,
        minHeight,
        maxHeight = 220,
        licenseKey = KEY,
        pinned: pinnedProp,
        defaultPinned = false,
        onPinnedChange,
        showPinned = false,
        pinnedLabel = 'Pinned',
        submitLabel = 'Comment',
        onSubmit,
        isSubmitting = false,
        showSubmit = true,
        showFormat = true,
        showMention = false,
        onMentionClick,
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        'aria-describedby': ariaDescribedBy,
        footer,
        expandWhenFilled = false,
    },
    ref
) {
    const reactId = useId()
    const editorId = id ?? `rich-text-editor-${reactId}`
    const errorId = `${editorId}-error`
    const isControlled = value !== undefined
    const isPinnedControlled = pinnedProp !== undefined

    const rootRef = useRef<HTMLDivElement>(null)
    const editorRef = useRef<Editor | null>(null)
    const pointerInsideRef = useRef(false)
    const blurTimerRef = useRef<number | null>(null)
    const pendingEditorFocusRef = useRef(false)

    const [expandedState, setExpanded] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const [formatOpen, setFormatOpen] = useState(false)
    const [activeFormats, setActiveFormats] = useState<Record<string, boolean>>({})
    const [uncontrolledHtml, setUncontrolledHtml] = useState(defaultValue)
    const [pinnedState, setPinnedState] = useState(defaultPinned)

    const html = isControlled ? value || '' : uncontrolledHtml
    const pinned = isPinnedControlled ? Boolean(pinnedProp) : pinnedState
    const hasError = Boolean(error)
    const errorMessage = typeof error === 'string' ? error : ''
    const empty = isEmptyHtml(html)

    // Prefilled or late-arriving content (draft, profile) must not stay squeezed into the collapsed single-line view.
    const expanded = expandedState || (expandWhenFilled && !empty)

    const editorConfig = useMemo(() => createEditorConfig({ licenseKey, placeholder }), [licenseKey, placeholder])

    const syncActiveFormats = () => {
        const editor = editorRef.current
        if (!editor) {
            setActiveFormats({})
            return
        }

        const next: Record<string, boolean> = {}
        for (const command of [
            'bold',
            'italic',
            'underline',
            'strikethrough',
            'bulletedList',
            'numberedList',
            'link',
        ]) {
            next[command] = Boolean(editor.commands.get(command)?.value)
        }
        setActiveFormats(next)
    }

    const setPinned = (next: boolean) => {
        if (!isPinnedControlled) setPinnedState(next)
        onPinnedChange?.(next)
    }

    const emitChange = (next: string) => {
        if (!isControlled) setUncontrolledHtml(next)
        onChange?.(next)
    }

    const collapseEditor = () => {
        setFormatOpen(false)
        setExpanded(false)
        setIsFocused(false)
        pendingEditorFocusRef.current = false
    }

    const requestEditorFocus = () => {
        pendingEditorFocusRef.current = true
        setExpanded(true)
        // Focus after expand styles apply — focusing during the collapsed→expanded
        // layout switch often leaves the editable focused-less (no caret).
        window.requestAnimationFrame(() => {
            if (!pendingEditorFocusRef.current) return
            pendingEditorFocusRef.current = false
            editorRef.current?.editing.view.focus()
        })
    }

    const runSubmit = async () => {
        if (disabled || isSubmitting || !onSubmit) return

        // Always read live editor data — controlled `value` can be stale if the
        // parent hasn't mirrored onChange back into props yet.
        const currentHtml = editorRef.current?.getData() ?? html
        if (isEmptyHtml(currentHtml)) return

        try {
            await onSubmit({
                html: currentHtml,
                text: stripHtml(currentHtml),
                pinned,
            })

            if (editorRef.current) {
                editorRef.current.setData('')
            }
            emitChange('')
            collapseEditor()
        } catch {
            // Keep editor open if submit fails.
        }
    }

    useEffect(() => {
        if (!isControlled || !editorRef.current) return

        const next = value || ''
        if (editorRef.current.getData() !== next) {
            editorRef.current.setData(next)
        }
    }, [isControlled, value])

    useImperativeHandle(ref, () => ({
        focus: () => {
            requestEditorFocus()
        },
        blur: () => {
            const view = editorRef.current?.editing.view
            view?.domRoots.forEach((root) => {
                if (document.activeElement === root) {
                    ;(document.activeElement as HTMLElement).blur()
                }
            })
        },
        getHTML: () => editorRef.current?.getData() ?? html,
        getText: () => stripHtml(editorRef.current?.getData() ?? html),
        clear: () => {
            if (!editorRef.current) return
            editorRef.current.setData('')
            emitChange('')
            setFormatOpen(false)
        },
        getEditor: () => editorRef.current,
        submit: () => {
            void runSubmit()
        },
    }))

    const describedBy =
        [ariaDescribedBy, hasError && errorMessage ? errorId : null].filter(Boolean).join(' ') || undefined

    const handleReady = (editor: Editor) => {
        editorRef.current = editor

        const initial = (isControlled ? value : defaultValue) || ''
        if (initial && editor.getData() !== initial) {
            editor.setData(initial)
        }

        editor.editing.view.document.on('selectionChange', syncActiveFormats)
        pinBalloonToolbarAboveSelection(editor)
        onReady?.(editor)
    }

    const handleAfterDestroy = () => {
        editorRef.current = null
    }

    const handleChange = (_event: EventInfo, editor: Editor) => {
        emitChange(editor.getData())
        syncActiveFormats()
    }

    const handleFormatCommand = (command: string) => {
        const editor = editorRef.current
        if (!editor || disabled) return

        editor.editing.view.focus()

        if (command === 'link') {
            setFormatOpen(false)
            const linkUI = editor.plugins.get('LinkUI') as { _showUI?: (forceVisible?: boolean) => void } | undefined
            linkUI?._showUI?.(true)
            return
        }

        editor.execute(command)
        emitChange(editor.getData())
        syncActiveFormats()
    }

    const handleFocusCapture = (event: FocusEvent<HTMLDivElement>) => {
        const previous = event.relatedTarget as Node | null
        if (previous && rootRef.current?.contains(previous)) return
        if (disabled) return

        if (blurTimerRef.current !== null) {
            window.clearTimeout(blurTimerRef.current)
            blurTimerRef.current = null
        }

        setExpanded(true)
        setIsFocused(true)
        onFocus?.(event)
    }

    const handleBlurCapture = (event: FocusEvent<HTMLDivElement>) => {
        const next = event.relatedTarget as Node | null
        if (next && rootRef.current?.contains(next)) return

        // Editor stays expanded until submit. Only close formatting menu on outside blur.
        if (blurTimerRef.current !== null) {
            window.clearTimeout(blurTimerRef.current)
        }

        blurTimerRef.current = window.setTimeout(() => {
            blurTimerRef.current = null

            if (pointerInsideRef.current) return
            if (rootRef.current?.contains(document.activeElement)) return

            setIsFocused(false)
            setFormatOpen(false)
            onBlur?.(event)
        }, 0)
    }

    const handleMouseDownCapture = (event: ReactMouseEvent<HTMLDivElement>) => {
        if (disabled) return

        const target = event.target as HTMLElement | null
        if (!target || !rootRef.current?.contains(target)) return

        pointerInsideRef.current = true

        if (blurTimerRef.current !== null) {
            window.clearTimeout(blurTimerRef.current)
            blurTimerRef.current = null
        }

        const isFormatControl = Boolean(target.closest('.CKEditor__formatWrap'))
        if (formatOpen && !isFormatControl) {
            setFormatOpen(false)
        }

        const isInteractive = Boolean(target.closest('button, input, label, a, textarea, select, [role="toolbar"]'))
        if (!isInteractive) {
            requestEditorFocus()
        }
    }

    const handleMouseUpCapture = () => {
        pointerInsideRef.current = false
    }

    useEffect(() => {
        return () => {
            if (blurTimerRef.current !== null) {
                window.clearTimeout(blurTimerRef.current)
            }
        }
    }, [])

    useEffect(() => {
        if (!formatOpen) return

        const handleOutsidePointerDown = (event: MouseEvent) => {
            const target = event.target as Node | null
            if (!target) return

            const formatWrap = rootRef.current?.querySelector('.CKEditor__formatWrap')
            if (formatWrap?.contains(target)) return

            setFormatOpen(false)
        }

        document.addEventListener('mousedown', handleOutsidePointerDown)
        return () => {
            document.removeEventListener('mousedown', handleOutsidePointerDown)
        }
    }, [formatOpen])

    return (
        <div
            ref={rootRef}
            className={cx(
                'CKEditor',
                expanded && 'CKEditor--expanded',
                isFocused && 'CKEditor--focused',
                formatOpen && 'CKEditor--formatOpen',
                disabled && 'CKEditor--disabled',
                hasError && 'CKEditor--error',
                empty && 'CKEditor--empty',
                className
            )}
            style={
                {
                    ...style,
                    '--rte-min-height': toCssSize(minHeight),
                    '--rte-max-height': toCssSize(maxHeight),
                } as CSSProperties
            }
            onFocusCapture={handleFocusCapture}
            onBlurCapture={handleBlurCapture}
            onMouseDownCapture={handleMouseDownCapture}
            onMouseUpCapture={handleMouseUpCapture}
            onMouseLeave={handleMouseUpCapture}
            onKeyDownCapture={(event) => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                    event.preventDefault()
                    void runSubmit()
                }
            }}
        >
            {name ? <input type="hidden" name={name} value={html} /> : null}

            <div className="CKEditor__surface">
                <div
                    className="CKEditor__editable"
                    id={editorId}
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                    aria-describedby={describedBy}
                    aria-invalid={hasError || undefined}
                >
                    <CKEditorBase
                        editor={DecoupledEditor}
                        data={html}
                        config={editorConfig}
                        disabled={disabled}
                        onReady={handleReady}
                        onAfterDestroy={handleAfterDestroy}
                        onChange={handleChange}
                        onFocus={() => {
                            setExpanded(true)
                            setIsFocused(true)
                        }}
                    />
                </div>

                <div
                    className={cx('CKEditor__footerShell', expanded && 'CKEditor__footerShell--open')}
                    aria-hidden={!expanded}
                >
                    <div className="CKEditor__footerShellInner">
                        <Footer
                            disabled={disabled || !expanded}
                            isEmpty={empty}
                            isSubmitting={isSubmitting}
                            formatOpen={formatOpen}
                            activeFormats={activeFormats}
                            pinned={pinned}
                            showPinned={showPinned}
                            pinnedLabel={pinnedLabel}
                            showFormat={showFormat}
                            showMention={showMention}
                            showSubmit={showSubmit}
                            submitLabel={submitLabel}
                            onToggleFormat={() => setFormatOpen((prev) => !prev)}
                            onFormatCommand={handleFormatCommand}
                            onMentionClick={onMentionClick}
                            onPinnedChange={setPinned}
                            onSubmit={() => {
                                void runSubmit()
                            }}
                        />
                    </div>
                </div>
            </div>

            {hasError && errorMessage ? (
                <div className="CKEditor__error" id={errorId} role="alert">
                    <ErrorIcon />
                    <span>{errorMessage}</span>
                </div>
            ) : null}

            {footer}

            <BalloonToolbarTooltip />
        </div>
    )
})

export default CKEditor
